import React, { useState } from 'react';
import i18next from 'core/i18n';
import { ColumnDefinition, RowDefinition, Table } from '@indigo-ag/component-module';

import {
  PhaseSection,
  PhaseSectionProps
} from 'features/brazil-grower/components/BrazilGrowerFinance/shared/PhaseSection';
import {
  IncomeStatementModal,
  IncomeStatementModalProps
} from './components/IncomeStatementModal/IncomeStatementModal';
import { IIncomeStatementInfo, CURRENCY_SYMBOL, createStylesHook } from 'shared';
import { currency, InputMaskCurrencyOptions } from '../../../../../../../shared/utils';

export interface IncomeStatementSectionProps
  extends Pick<PhaseSectionProps, 'disabled'>,
    Pick<IncomeStatementModalProps, 'updateCredit'> {
  incomeStatementInfo?: IIncomeStatementInfo;
}

interface IncomeStatementSectionSummaryProps extends Pick<IncomeStatementSectionProps, 'incomeStatementInfo'> {}

interface FormattedIncomeStatementDataA {
  yearIrpf: string;
  patrimony: string;
  rural: string;
  urban: string;
  investments: string;
}

interface FormattedIncomeStatementDataB {
  debtsAndLiens: string;
  grossRevenue: string;
  defrayal: string;
  result: string;
  taxableIncome: string;
}

interface FormattedIncomeStatementDataC {
  exploredAreaCode: string;
  exploredHectares: string;
}

const styles = createStylesHook({
  layout: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '280px'
  }
});

type ColumnIdA = 'yearIrpf' | 'patrimony' | 'rural' | 'urban' | 'investments';

type ColumnIdB = 'debtsAndLiens' | 'grossRevenue' | 'defrayal' | 'result' | 'taxableIncome';

type ColumnIdC = 'exploredAreaCode' | 'exploredHectares';

const columnDefinitionA: ColumnDefinition<FormattedIncomeStatementDataA, ColumnIdA>[] = [
  {
    id: 'yearIrpf',
    header: i18next.t('yearIrpf'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.yearIrpf} />
  },
  {
    id: 'patrimony',
    header: i18next.t('patrimony'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.patrimony} />
  },
  {
    id: 'rural',
    header: i18next.t('rural'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.rural} />
  },
  {
    id: 'urban',
    header: i18next.t('urban'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.urban} />
  },
  {
    id: 'investments',
    header: i18next.t('investments'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.investments} />
  }
];

const columnDefinitionB: ColumnDefinition<FormattedIncomeStatementDataB, ColumnIdB>[] = [
  {
    id: 'debtsAndLiens',
    header: i18next.t('debtsAndLiens'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.debtsAndLiens} />
  },
  {
    id: 'grossRevenue',
    header: i18next.t('grossRevenue'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.grossRevenue} />
  },
  {
    id: 'defrayal',
    header: i18next.t('defrayal'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.defrayal} />
  },
  {
    id: 'result',
    header: i18next.t('result'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.result} />
  },
  {
    id: 'taxableIncome',
    header: i18next.t('taxableIncome'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.taxableIncome} />
  }
];

const columnDefinitionC: ColumnDefinition<FormattedIncomeStatementDataC, ColumnIdC>[] = [
  {
    id: 'exploredAreaCode',
    header: i18next.t('exploredAreaCode'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.exploredAreaCode} />
  },
  {
    id: 'exploredHectares',
    header: i18next.t('hectaresExplored'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.exploredHectares} />
  }
];

const rowDefinitionA: RowDefinition<FormattedIncomeStatementDataA, ColumnIdA> = {
  rowId: ({ row }) => `${row.yearIrpf}`
};

const rowDefinitionB: RowDefinition<FormattedIncomeStatementDataB, ColumnIdB> = {
  rowId: ({ row }) => `${row.debtsAndLiens}`
};

const rowDefinitionC: RowDefinition<FormattedIncomeStatementDataC, ColumnIdC> = {
  rowId: ({ row }) => `${row.exploredAreaCode}`
};

export const IncomeStatementSectionSummary: React.FunctionComponent<IncomeStatementSectionSummaryProps> = ({
  incomeStatementInfo
}) => {
  /**
   * Styles
   */
  const classes = styles();

  const displayNumericField = (number?: string | number, showCurrency: boolean = true) => {
    if (!number || isNaN(parseFloat(number?.toString())) || parseFloat(number?.toString()) <= 0) {
      return '';
    }
    const currencyOptions: InputMaskCurrencyOptions | undefined = showCurrency
      ? { currencySymbol: CURRENCY_SYMBOL.BR }
      : {};
    return currency.inputMaskCurrency(number, currencyOptions);
  };

  const dataTableA: FormattedIncomeStatementDataA[] = [
    {
      yearIrpf: incomeStatementInfo?.returnOfIncomeYearIRPF || '',
      patrimony: displayNumericField(incomeStatementInfo?.returnOfIncomePatrimony),
      rural: displayNumericField(incomeStatementInfo?.returnOfIncomeRural),
      urban: displayNumericField(incomeStatementInfo?.returnOfIncomeUrban),
      investments: displayNumericField(incomeStatementInfo?.returnOfIncomeInvestment, undefined)
    }
  ];

  const dataTableB: FormattedIncomeStatementDataB[] = [
    {
      debtsAndLiens: displayNumericField(incomeStatementInfo?.returnOfIncomeDebts),
      grossRevenue: displayNumericField(incomeStatementInfo?.returnOfIncomeGrossRevenue),
      defrayal: displayNumericField(incomeStatementInfo?.returnOfIncomeCosting),
      result: displayNumericField(incomeStatementInfo?.returnOfIncomeResult),
      taxableIncome: displayNumericField(incomeStatementInfo?.returnOfIncomeTaxableIncome)
    }
  ];

  const dataTableC: FormattedIncomeStatementDataC[] = [
    {
      exploredAreaCode: incomeStatementInfo?.returnOfIncomeExploredAreaCode || '',
      exploredHectares: displayNumericField(incomeStatementInfo?.returnOfIncomeExploredHectares, false)
    }
  ];

  return incomeStatementInfo ? (
    <div className={classes.layout}>
      <Table
        label="Income Statement Part I"
        data={dataTableA}
        columnDefinition={columnDefinitionA}
        rowDefinition={rowDefinitionA}
      />
      <Table
        label="Income Statement Part II"
        data={dataTableB}
        columnDefinition={columnDefinitionB}
        rowDefinition={rowDefinitionB}
      />
      <Table
        label="Income Statement Part III"
        data={dataTableC}
        columnDefinition={columnDefinitionC}
        rowDefinition={rowDefinitionC}
      />
    </div>
  ) : null;
};

export const IncomeStatementSection: React.FunctionComponent<IncomeStatementSectionProps> = ({
  incomeStatementInfo,
  updateCredit,
  children,
  ...phaseSectionProps
}) => {
  /**
   * State
   */
  const [modalOpen, setModalOpen] = useState(false);

  const openIncomeStatementModal = (): void => setModalOpen(true);

  const closeIncomeStatementModal = (): void => setModalOpen(false);

  return (
    <>
      <PhaseSection
        {...phaseSectionProps}
        open={!!incomeStatementInfo}
        onClickEdit={openIncomeStatementModal}
        title={i18next.t('incomeTax')}
        renderSeparatorBefore
      >
        <IncomeStatementSectionSummary incomeStatementInfo={incomeStatementInfo} />
      </PhaseSection>
      <IncomeStatementModal
        open={modalOpen}
        closeModal={closeIncomeStatementModal}
        incomeStatementInfo={incomeStatementInfo}
        updateCredit={updateCredit}
      />
    </>
  );
};
